import { motion } from "framer-motion";
import { Reveal, StatusBadge, GoldButton, EASE } from "./shared";
import { IMG, SEVENTH_PARKVIEW, SEVENTH_PARKVIEW_GALLERY } from "../data/site";
import { MapPin, CheckCircle2, ExternalLink } from "lucide-react";

export function SeventhParkview({ onEnquire }) {
  return (
    <div data-testid="seventh-parkview-panel">
      <Reveal delay={0.05}>
        <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed max-w-2xl mb-6">
          A 34.61-acre plotted and villa sector within Gaur Yamuna City, flanked by an
          international-standard cricket ground on one side and the 108-ft Lord Krishna statue
          on the other. RERA No. {SEVENTH_PARKVIEW.rera}.
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="flex items-center gap-3 mb-10 px-5 py-3.5 border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] rounded-[2px] w-fit" data-testid="seventh-parkview-office-note">
          <MapPin size={15} className="text-[#D4AF37] shrink-0" />
          <p className="text-[12px] text-[#A1A1AA]">
            Radiance Realtors&rsquo; registered office is located inside 7th Park View &mdash; our
            team knows this sector first-hand.
          </p>
        </div>
      </Reveal>

        <div className="grid lg:grid-cols-5 gap-6 mb-14">
          <Reveal className="lg:col-span-3" delay={0.08}>
            <div className="relative h-[300px] lg:h-[440px] overflow-hidden rounded-[2px] border border-[#D4AF37]/20" data-testid="seventh-parkview-hero">
              <img src={IMG.sevenPvAerial} alt="7th Park View aerial, plotted sectors" className="w-full h-full object-cover img-cinematic" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/10" />
              <div className="absolute bottom-0 inset-x-0 p-6 lg:p-8 flex items-end justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Indicative Price Range</p>
                  <p className="font-serif-lux text-3xl text-[#F5F5F0]">{SEVENTH_PARKVIEW.priceRange}</p>
                  <p className="text-[11px] text-[#71717A] mt-1">Per public listings - verify current rate with our desk</p>
                </div>
                <StatusBadge status="Operational" />
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-2 grid grid-cols-2 gap-4" data-testid="seventh-parkview-stats">
            {SEVENTH_PARKVIEW.stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                className="border border-[#D4AF37]/15 bg-[#111111] rounded-[2px] p-5 flex flex-col justify-center"
              >
                <p className="font-serif-lux text-3xl lg:text-4xl text-[#F5F5F0]">{s.value}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-1.5 leading-snug">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 mb-16">
          <Reveal className="lg:col-span-2" delay={0.1}>
            <div className="card-lux p-7 rounded-[2px] h-full" data-testid="seventh-parkview-facts">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-5">Project Facts</p>
              <dl className="divide-y divide-[#D4AF37]/10">
                {SEVENTH_PARKVIEW.facts.map(([k, v]) => (
                  <div key={k} className="grid sm:grid-cols-[110px_1fr] gap-1 sm:gap-5 py-3">
                    <dt className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80 pt-0.5">{k}</dt>
                    <dd className="text-[12px] text-[#A1A1AA] leading-relaxed">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={0.15}>
            <div className="border border-[#D4AF37]/15 rounded-[2px] overflow-hidden h-full" data-testid="seventh-parkview-plot-sizes">
              <div className="grid grid-cols-2 bg-[#D4AF37]/[0.06] px-5 py-4 text-[10px] uppercase tracking-[0.25em]">
                <span className="text-[#71717A]">Plot Size</span>
                <span className="text-[#F3E5AB]">Number of Plots</span>
              </div>
              {SEVENTH_PARKVIEW.plotSizes.map((row, i) => (
                <div key={row.size} className={`grid grid-cols-2 gap-4 px-5 py-3.5 text-[12px] sm:text-[13px] leading-relaxed ${i % 2 ? "bg-[#0D0D0D]" : "bg-[#111111]"}`}>
                  <span className="text-[#A1A1AA]">{row.size}</span>
                  <span className="text-[#F5F5F0]">{row.count}</span>
                </div>
              ))}
              <p className="px-5 py-3 text-[10px] text-[#52525b] leading-relaxed bg-[#0A0A0A]">
                As per the sanctioned 7th Park View site plan. Plot entry, orientation and exact
                dimensions vary by location within the sector.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-6">Villa Clusters Within 7th Park View</p>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-5 mb-16" data-testid="seventh-parkview-villa-clusters">
          {SEVENTH_PARKVIEW.villaClusters.map((v, i) => (
            <Reveal key={v.name} delay={0.08 + i * 0.06}>
              <div className="border border-[#D4AF37]/15 bg-[#111111] rounded-[2px] p-6 h-full">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif-lux text-xl text-[#F5F5F0]">{v.name}</h3>
                  <StatusBadge status={v.status} />
                </div>
                <p className="text-[13px] text-[#A1A1AA] leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          <Reveal delay={0.1}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-5">Amenities</p>
              <ul className="grid sm:grid-cols-2 gap-3.5" data-testid="seventh-parkview-amenities">
                {SEVENTH_PARKVIEW.amenities.map((a) => (
                  <li key={a} className="flex gap-3 text-[13px] text-[#A1A1AA] leading-relaxed">
                    <CheckCircle2 size={15} strokeWidth={1.5} className="text-[#D4AF37] mt-0.5 shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative h-[280px] overflow-hidden rounded-[2px] border border-[#D4AF37]/20">
              <img src={IMG.sevenPvLayout} alt="7th Park View site plan and plot layout" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-5 text-[10px] uppercase tracking-[0.25em] text-[#F3E5AB]">Sanctioned Site Plan</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-14" data-testid="seventh-parkview-gallery">
            {SEVENTH_PARKVIEW_GALLERY.map((g, i) => (
              <motion.div
                key={g.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: EASE }}
                className="relative aspect-square overflow-hidden rounded-[2px] border border-[#D4AF37]/12 group"
              >
                <img src={g.src} alt={g.caption} className="w-full h-full object-cover img-cinematic transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <p className="absolute bottom-2 left-2 right-2 text-[9px] leading-tight text-[#F3E5AB] opacity-0 group-hover:opacity-100 transition-opacity duration-500">{g.caption}</p>
              </motion.div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <p className="font-serif-lux text-2xl text-[#F5F5F0] mb-2">Considering a plot or villa in 7th Park View?</p>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                Our desk is based inside this sector and can walk you through live plots, resale
                villas, RERA documentation and the exact site plan before you decide.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <GoldButton data-testid="seventh-parkview-enquire-btn" onClick={onEnquire}>
                Enquire About 7th Park View
              </GoldButton>
              <a
                href="https://www.up-rera.in/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="seventh-parkview-rera-link"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 rounded-[2px] transition-colors"
              >
                Verify on UP RERA <ExternalLink size={14} />
              </a>
            </div>
          </div>
          <p className="mt-8 text-[11px] text-[#52525b] leading-relaxed max-w-4xl" data-testid="seventh-parkview-disclaimer">
            Facts compiled from the developer&rsquo;s published specifications and public listing
            portals; figures, possession status and pricing change over time and vary by individual
            plot or villa. Radiance Realtors is an independent channel partner and not the developer -
            please verify RERA registration, title and current price directly before any transaction.
          </p>
        </Reveal>
    </div>
  );
}
